import React, {
  useState,
  useEffect,
} from "react"

import {
  PlusIcon,
  MinusIcon,
  TrashIcon,
  PencilSquareIcon,
  EnvelopeIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"

import PulseLoader from "react-spinners/PulseLoader"

import EtBtnFormBasic
  from "@/components/common/buttons/EtBtnFormBasic"

import Modal
  from "@/components/common/modal/Modal"

import {
  useGetOrgIntentionCandidatesQuery,
  useCreateNewOrgIntentionCandidateMutation,
  useUpdateOrgIntentionCandidateMutation,
  useMarkOrgIntentionCandidateInvitedMutation,
  useDeleteOrgIntentionCandidateMutation,
} from "@/features/orgIntentionBoards/orgIntentionCandidatesApiSlice"

const EtOrgIntentionCandidateManager = ({
  intentionBoardId,
}) => {

  // First Created - 04 Sep 2026
  // Author - Atul Pandey
  // Purpose - Manage nominated candidates for a Trinity Intention Board.
  // Updated - 08 Sep 2026 - Bulk Candidate creation via text upload
  // Updated - 23 Sep 2026 - Refined scroll and candidates filter based on status - i.e., submitted, draft, nominated, accessed

  /*
  |--------------------------------------------------------------------------
  | 1 - State
  |--------------------------------------------------------------------------
  */

  const [
    showManager,
    setShowManager,
  ] =
    useState(true)

  const [
    editCandidateId,
    setEditCandidateId,
  ] =
    useState(null)

  const [
    deleteCandidateId,
    setDeleteCandidateId,
  ] =
    useState(null)

  const [
    isDeleteModalOpen,
    setIsDeleteModalOpen,
  ] =
    useState(false)

  /*
  |--------------------------------------------------------------------------
  | Bulk Candidate Input - 08 Sep 2026
  |--------------------------------------------------------------------------
  */

  const [
    bulkCandidateText,
    setBulkCandidateText,
    ] =
    useState(
        ""
    )

  const [
    parsedBulkCandidates,
    setParsedBulkCandidates,
    ] =
    useState(
        []
    )

  const [
    bulkCandidateError,
    setBulkCandidateError,
    ] =
    useState(
        ""
    )

  const [
    isAddingBulkCandidates,
    setIsAddingBulkCandidates,
    ] =
    useState(
        false
    )
  
  /*
  |--------------------------------------------------------------------------
  | Candidate Status Filter - 23 Sep 2026
  |--------------------------------------------------------------------------
  */

  const [
    candidateStatusFilter,
    setCandidateStatusFilter,
  ] =
    useState(
      "all"
    )
    
  /*
  |--------------------------------------------------------------------------
  | Parse Bulk Candidate Text - 08 Sep 2026
  |--------------------------------------------------------------------------
  */

  const parseBulkCandidateText =
    text => {

    const entries =
      String(
        text ||
        ""
      )
        .split(
          ";"
        )
        .map(
          item =>
            item.trim()
        )
        .filter(
          Boolean
        )

    const parsedCandidates = []

    for (
      const entry of entries
    ) {

      const parts =
        entry
          .split(
            /\s+/
          )
          .filter(
            Boolean
          )

      if (
        parts.length <
        2
      ) {

        continue

      }

      const candidateEmail =
        parts[
          parts.length -
          1
        ]
          .trim()

      const candidateName =
        parts
          .slice(
            0,
            -1
          )
          .join(
            " "
          )
          .trim()

      parsedCandidates.push({

        candidateName,

        candidateEmail,

      })

    }

    return parsedCandidates

  }

  /*
  |--------------------------------------------------------------------------
  | Validate Email - 08 Sep 2026
  |--------------------------------------------------------------------------
  */

  const isValidEmail =
    value => {

        return (
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            .test(
            String(
                value ||
                ""
            )
                .trim()
            )
        )

  }
  
  /*
  |--------------------------------------------------------------------------
  | 2 - Get Candidates
  |--------------------------------------------------------------------------
  */

  const {
    data:
      orgIntentionCandidatesData,

    isLoading:
      isLoadingCandidates,

    isError:
      isCandidatesError,

    error:
      candidatesError,

  } =
    useGetOrgIntentionCandidatesQuery({
      intentionBoardId,
    })

  /*
  |--------------------------------------------------------------------------
  | 3 - Mutations
  |--------------------------------------------------------------------------
  */

  const [
    createNewOrgIntentionCandidate,
    {
      isLoading:
        isCreating,

      isSuccess:
        isCreateSuccess,

      isError:
        isCreateError,

      error:
        createError,
    },
  ] =
    useCreateNewOrgIntentionCandidateMutation()

  const [
    updateOrgIntentionCandidate,
    {
      isLoading:
        isUpdating,

      isSuccess:
        isUpdateSuccess,

      isError:
        isUpdateError,

      error:
        updateError,
    },
  ] =
    useUpdateOrgIntentionCandidateMutation()

  const [
    markOrgIntentionCandidateInvited,
    {
      isLoading:
        isMarkingInvited,

      isError:
        isInviteError,

      error:
        inviteError,
    },
  ] =
    useMarkOrgIntentionCandidateInvitedMutation()

  const [
    deleteOrgIntentionCandidate,
    {
      isLoading:
        isDeleting,

      isError:
        isDeleteError,

      error:
        deleteError,
    },
  ] =
    useDeleteOrgIntentionCandidateMutation()

  /*
  |--------------------------------------------------------------------------
  | 4 - Normalize Candidates - updated 23 Sep 2026
  |--------------------------------------------------------------------------
  */

  const orgIntentionCandidates =
    orgIntentionCandidatesData
      ?.ids
      ?.map(
        id =>
          orgIntentionCandidatesData
            ?.entities[id]
      )
      ?.filter(
        Boolean
      )
      ?.slice()
      ?.sort(
      (
        a,
        b
      ) => {

        /*
        |--------------------------------------------------------------------------
        | Submitted At
        |--------------------------------------------------------------------------
        */

        const aSubmittedAt =
          a
            ?.submittedAt
            ? new Date(
                a.submittedAt
              ).getTime()
            : Number.MAX_SAFE_INTEGER


        const bSubmittedAt =
          b
            ?.submittedAt
            ? new Date(
                b.submittedAt
              ).getTime()
            : Number.MAX_SAFE_INTEGER


        if (
          aSubmittedAt !==
          bSubmittedAt
        ) {

          return (
            aSubmittedAt -
            bSubmittedAt
          )

        }


        /*
        |--------------------------------------------------------------------------
        | Fallback - Candidate Name
        |--------------------------------------------------------------------------
        */

        return (
          a
            ?.candidateName ||
          ""
        )
          .localeCompare(
            b
              ?.candidateName ||
            ""
          )

      }
    ) ||
  []

  // 23 Sep 2026
  const getCandidateStatusCount =
    status => {

      if (
        status ===
        "all"
      ) {

        return orgIntentionCandidates
          ?.length ||
          0

      }


      return (
        orgIntentionCandidates
          ?.filter(
            candidate =>
              candidate
                ?.candidateStatus ===
              status
          )
          ?.length ||
        0
      )

  }   


  /*
  |--------------------------------------------------------------------------
  | Filter Candidates By Status
  |--------------------------------------------------------------------------
  */

  const filteredOrgIntentionCandidates =
    candidateStatusFilter ===
    "all"
      ? orgIntentionCandidates
      : orgIntentionCandidates
          .filter(
            candidate =>
              candidate
                ?.candidateStatus ===
              candidateStatusFilter
          )

  /*
  |--------------------------------------------------------------------------
  | 5 - Initial Form State
  |--------------------------------------------------------------------------
  */

  const initialFormState = {

    candidateName:
      "",

    candidateEmail:
      "",

    isActive:
      true,

  }

  const [
    formData,
    setFormData,
  ] =
    useState(
      initialFormState
    )

  /*
  |--------------------------------------------------------------------------
  | 6 - Reset After Save
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (
      isCreateSuccess ||
      isUpdateSuccess
    ) {

      setEditCandidateId(
        null
      )

      setFormData(
        initialFormState
      )

    }

  }, [
    isCreateSuccess,
    isUpdateSuccess,
  ])

  /*
  |--------------------------------------------------------------------------
  | 7 - Handle Change
  |--------------------------------------------------------------------------
  */

  const handleChange =
    e => {

      const {
        name,
        value,
        type,
        checked,
      } =
        e.target

      setFormData(
        prev => ({

          ...prev,

          [name]:
            type ===
            "checkbox"
              ? checked
              : value,

        })
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 8 - Edit Candidate
  |--------------------------------------------------------------------------
  */

  const handleEditCandidate =
    candidate => {

      setEditCandidateId(
        candidate._id
      )

      setFormData({

        candidateName:
          candidate
            ?.candidateName ||
          "",

        candidateEmail:
          candidate
            ?.candidateEmail ||
          "",

        isActive:
          candidate
            ?.isActive ??
          true,

      })

    }

  /*
  |--------------------------------------------------------------------------
  | 9 - Cancel Edit
  |--------------------------------------------------------------------------
  */

  const handleCancelEdit =
    () => {

      setEditCandidateId(
        null
      )

      setFormData(
        initialFormState
      )

    }

  /*
  |--------------------------------------------------------------------------
  | 10 - Submit Candidate
  |--------------------------------------------------------------------------
  */

  const handleSubmit =
    async e => {

      e.preventDefault()

      const candidateData = {

        intentionBoardId,

        candidateName:
          formData.candidateName,

        candidateEmail:
          formData.candidateEmail,

        isActive:
          formData.isActive,

      }

      try {

        if (
          editCandidateId
        ) {

          await updateOrgIntentionCandidate({

            intentionCandidateId:
              editCandidateId,

            ...candidateData,

          })
            .unwrap()

        } else {

          await createNewOrgIntentionCandidate(
            candidateData
          )
            .unwrap()

        }

      } catch (
        error
      ) {

        console.error(
          error
        )

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 11 - Mark Invited
  |--------------------------------------------------------------------------
  */

  const handleMarkInvited =
    async intentionCandidateId => {

      try {

        await markOrgIntentionCandidateInvited({

          intentionCandidateId,

          intentionBoardId,

        })
          .unwrap()

      } catch (
        error
      ) {

        console.error(
          error
        )

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 12 - Delete Candidate
  |--------------------------------------------------------------------------
  */

  const handleOpenDeleteModal =
    intentionCandidateId => {

      setDeleteCandidateId(
        intentionCandidateId
      )

      setIsDeleteModalOpen(
        true
      )

    }

  const handleCloseDeleteModal =
    () => {

      setDeleteCandidateId(
        null
      )

      setIsDeleteModalOpen(
        false
      )

    }

  const handleDeleteCandidate =
    async e => {

      e.preventDefault()

      if (
        !deleteCandidateId
      ) {
        return
      }

      try {

        await deleteOrgIntentionCandidate({

          intentionCandidateId:
            deleteCandidateId,

          intentionBoardId,

        })
          .unwrap()

      } catch (
        error
      ) {

        console.error(
          error
        )

      } finally {

        handleCloseDeleteModal()

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 13 - Candidate Status
  |--------------------------------------------------------------------------
  */

  const getCandidateStatusClass =
    candidateStatus => {

      switch (
        candidateStatus
      ) {

        case "submitted":
          return "bg-lime-100 text-lime-700"

        case "draft":
          return "bg-yellow-100 text-yellow-700"

        case "accessed":
          return "bg-blue-100 text-blue-700"

        case "invited":
          return "bg-orange-100 text-orange-700"

        case "nominated":
          return "bg-gray-100 text-gray-700"

        case "expired":
          return "bg-red-100 text-red-700"

        case "withdrawn":
          return "bg-gray-200 text-gray-500"

        default:
          return "bg-gray-100 text-gray-600"

      }

    }

  /*
  |--------------------------------------------------------------------------
  | 14 - Format Date
  |--------------------------------------------------------------------------
  */

  const formatDate =
    dateValue => {

      if (
        !dateValue
      ) {

        return "Not Available"

      }

      return new Date(
        dateValue
      )
        .toLocaleString()

    }

  /*
  |--------------------------------------------------------------------------
  | Handle Bulk Candidate Parse - 08 Sep 2026
  |--------------------------------------------------------------------------
  */

  const handleParseBulkCandidates =
   () => {

    setBulkCandidateError(
      ""
    )

    const parsedCandidates =
      parseBulkCandidateText(
        bulkCandidateText
      )

    if (
      !parsedCandidates.length
    ) {

      setParsedBulkCandidates(
        []
      )

      setBulkCandidateError(
        "No candidates could be identified from the text entered."
      )

      return

    }

    const invalidCandidates =
      parsedCandidates
        .filter(
          candidate =>
            !candidate
              ?.candidateName ||
            !isValidEmail(
              candidate
                ?.candidateEmail
            )
        )

    if (
      invalidCandidates.length
    ) {

      setParsedBulkCandidates(
        parsedCandidates
      )

      setBulkCandidateError(
        `${invalidCandidates.length} candidate record(s) have an invalid name or email address.`
      )

      return

    }

    setParsedBulkCandidates(
      parsedCandidates
    )

  }

  /*
  |--------------------------------------------------------------------------
  | Add Bulk Candidates
  |--------------------------------------------------------------------------
  */

  const handleAddBulkCandidates =
    async e => {

    e.preventDefault()

    if (
      !parsedBulkCandidates.length
    ) {

      return

    }

    setBulkCandidateError(
      ""
    )

    setIsAddingBulkCandidates(
      true
    )

    try {

      for (
        const candidate of
          parsedBulkCandidates
      ) {

        await createNewOrgIntentionCandidate({

          intentionBoardId,

          candidateName:
            candidate
              .candidateName,

          candidateEmail:
            candidate
              .candidateEmail,

        })
          .unwrap()

      }

      setBulkCandidateText(
        ""
      )

      setParsedBulkCandidates(
        []
      )

    } catch (
      error
    ) {

      console.error(
        error
      )

      setBulkCandidateError(
        error
          ?.data
          ?.message ||
        "Unable to add one or more candidates."
      )

    } finally {

      setIsAddingBulkCandidates(
        false
      )

    }

  }

  /*
  |--------------------------------------------------------------------------
  | 15 - Loading / Errors
  |--------------------------------------------------------------------------
  */

  const isLoading =
    isLoadingCandidates ||
    isCreating ||
    isUpdating ||
    isMarkingInvited ||
    isDeleting

  const mutationError =
    createError ||
    updateError ||
    inviteError ||
    deleteError

  const mutationIsError =
    isCreateError ||
    isUpdateError ||
    isInviteError ||
    isDeleteError

  /*
  |--------------------------------------------------------------------------
  | 16 - Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="m-1 p-1 bg-white rounded">

      {/* Header */}

      <div className="flex justify-between items-center m-1 p-1">

        <div className="flex justify-start items-center">

           <div
            onClick={
              () =>
                setShowManager(
                  prev =>
                    !prev
                )
            }
            className="m-1 p-1 cursor-pointer rounded-full h-7 w-7 flex justify-center items-center hover:bg-gray-100"
          >

            {showManager ? (

              <MinusIcon className="h-5 w-5 text-orange-600" />

            ) : (

              <PlusIcon className="h-4 w-4 text-orange-600" />

            )}

          </div>

          <p className="font-bold text-lg mx-2">
            Intention Candidates
          </p>

         

        </div>

      </div>

      {showManager && (

        <div>

          {/* Loading */}

          {isLoading && (

            <PulseLoader
              color={"#ea580c"}
              size={6}
            />

          )}

          {/* Query Error */}

          {isCandidatesError && (

            <p className="text-orange-700 text-xs p-2">

              {
                candidatesError
                  ?.data
                  ?.message ||
                "Unable to load Intention Board candidates"
              }

            </p>

          )}

          {/* Mutation Error */}

          {mutationIsError && (

            <p className="text-orange-700 text-xs p-2">

              {
                mutationError
                  ?.data
                  ?.message ||
                "Unable to manage Intention Board candidate"
              }

            </p>

          )}

          {/* Existing Candidates */}

          <div className="bg-gray-200 rounded m-1 p-1">

            <p className="font-bold text-xs m-2">
              Nominated Candidates
            </p>

            {/* 23 Sep 2026 */}

            {/*
            |--------------------------------------------------------------------------
            | Candidate Status Filters
            |--------------------------------------------------------------------------
            */}

            <div className="flex justify-start items-center gap-6 mx-2 mb-2">

              <div className="flex justify-start items-center flex-wrap gap-1">

                {[
                  {
                    label:
                      "All",

                    value:
                      "all",
                  },
                  {
                    label:
                      "Nominated",

                    value:
                      "nominated",
                  },
                  {
                    label:
                      "Accessed",

                    value:
                      "accessed",
                  },
                  {
                    label:
                      "Draft",

                    value:
                      "draft",
                  },
                  {
                    label:
                      "Submitted",

                    value:
                      "submitted",
                  },
                ]
                  .map(
                    filter => {

                      const isSelected =
                        candidateStatusFilter ===
                        filter.value


                      return (

                        <button
                          key={
                            filter.value
                          }
                          type="button"
                          onClick={
                            () =>
                              setCandidateStatusFilter(
                                filter.value
                              )
                          }
                          className={`
                            rounded-full
                            px-3
                            py-1
                            text-[9px]
                            font-bold
                            cursor-pointer
                            ${
                              isSelected
                                ? "bg-orange-600 text-white"
                                : "bg-white text-gray-600 hover:bg-orange-50"
                            }
                          `}
                        >
                          {
                            filter.label
                          }
                        </button>

                      )

                    }
                  )}

              </div>


              <div
                className="
                  flex
                  items-center
                  justify-center
                  w-10
                  h-10
                  shrink-0
                  rounded-full
                  bg-orange-50
                  text-orange-700
                  text-md
                  border
                  border-orange-300
                  font-bold
                "
              >
                {
                  getCandidateStatusCount(
                    candidateStatusFilter
                  )
                }
              </div>

            </div>

            {/* {23 Sep 2026 - switched from orgIntentionCandidates to filteredOrgIntentionCandidates*/}
            {filteredOrgIntentionCandidates
              ?.length === 0 && (

              <div className="bg-white rounded m-1 p-2">

                {/* 23 Sep 2026 */}
                <p className="text-xs text-gray-500">
                  {
                    candidateStatusFilter ===
                    "all"
                      ? "No candidates have been nominated for this Intention Board."
                      : `No ${candidateStatusFilter} candidates found.`
                  }
                </p>

                {/* <p className="text-xs text-gray-500">
                  No candidates have been nominated for this Intention Board.
                </p> */}

              </div>

            )}

            {/* 23 Sep 2026 - scroll */}
            <div
              className="
                max-h-[420px]
                overflow-y-auto
                pr-1
              "
            >
            {/* {23 Sep 2026 - switched from orgIntentionCandidates to filteredOrgIntentionCandidates*/}
            {filteredOrgIntentionCandidates
                ?.map(
                  (
                    candidate,
                    index
                  ) => (

                    <div
                      key={
                        candidate._id
                      }
                      className="bg-white rounded m-1 p-2"
                    >

                      <div className="flex justify-between items-start">

                        <div className="flex-1">

                          <div className="flex justify-start items-center flex-wrap">

                            <p className="font-bold text-xs">

                              {
                                index + 1
                              }
                              .{" "}
                              {
                                candidate
                                  ?.candidateName
                              }

                            </p>

                            <p
                              className={`text-[9px] rounded-full mx-2 px-2 py-1 ${
                                getCandidateStatusClass(
                                  candidate
                                    ?.candidateStatus
                                )
                              }`}
                            >

                              {
                                candidate
                                  ?.candidateStatus ||
                                "nominated"
                              }

                            </p>

                            <p
                              className={`text-[9px] rounded-full mx-1 px-2 py-1 ${
                                candidate
                                  ?.isActive
                                  ? "bg-lime-100 text-lime-700"
                                  : "bg-gray-200 text-gray-500"
                              }`}
                            >

                              {
                                candidate
                                  ?.isActive
                                  ? "Active"
                                  : "Inactive"
                              }

                            </p>

                          </div>

                          <p className="text-[10px] text-gray-600 mt-1">
                            {
                              candidate
                                ?.candidateEmail
                            }
                          </p>

                          <div className="flex justify-start items-center flex-wrap mt-1">

                            <p className="text-[9px] text-gray-500 mr-3">

                              Nominated: {
                                formatDate(
                                  candidate
                                    ?.nominatedAt
                                )
                              }

                            </p>

                            {candidate
                              ?.invitedAt && (

                              <p className="text-[9px] text-gray-500 mr-3">

                                Invited: {
                                  formatDate(
                                    candidate
                                      ?.invitedAt
                                  )
                                }

                              </p>

                            )}

                            {candidate
                              ?.submittedAt && (

                              <p className="text-[9px] text-gray-500">

                                Submitted: {
                                  formatDate(
                                    candidate
                                      ?.submittedAt
                                  )
                                }

                              </p>

                            )}

                          </div>

                        </div>

                        {/* Actions */}

                        <div className="flex justify-start items-center">

                          {/* Mark Invited */}

                          {candidate
                            ?.isActive &&
                            candidate
                              ?.candidateStatus !==
                              "submitted" && (

                            <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-gray-100">

                              <EnvelopeIcon
                                onClick={
                                  () =>
                                    handleMarkInvited(
                                      candidate._id
                                    )
                                }
                                className="h-4 w-4 text-orange-600 cursor-pointer"
                              />

                            </div>

                          )}

                          {/* Edit */}

                          <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-gray-100">

                            <PencilSquareIcon
                              onClick={
                                () =>
                                  handleEditCandidate(
                                    candidate
                                  )
                              }
                              className="h-4 w-4 text-orange-600 cursor-pointer"
                            />

                          </div>

                          {/* Delete */}

                          {(
                            candidate
                              ?.candidateStatus ===
                              "nominated" ||
                            candidate
                              ?.candidateStatus ===
                              "invited"
                          ) && (

                            <div className="rounded-full flex justify-center items-center h-6 w-6 hover:bg-gray-100">

                              <TrashIcon
                                onClick={
                                  () =>
                                    handleOpenDeleteModal(
                                      candidate._id
                                    )
                                }
                                className="h-4 w-4 text-orange-600 cursor-pointer"
                              />

                            </div>

                          )}

                        </div>

                      </div>

                    </div>

                  )
                )}
            </div>

          </div>

          {/* 08 Sep 2026 */}

          {/*
           |--------------------------------------------------------------------------
           | Bulk Candidate Entry
           |--------------------------------------------------------------------------
           */}

           <div className="bg-gray-200 rounded m-1 p-1">

            <div className="m-1 p-1">

                <p className="font-bold text-xs">
                Add Multiple Candidates
                </p>

                <p className="text-[9px] text-gray-500 mt-1">
                Enter candidates as Name Email, separated by semicolons.
                </p>

                <p className="text-[9px] text-gray-500 mt-1">
                Example: John Doe John.Doe@abc.com; Jane Doe Jane.Doe@abc.com;
                </p>

            </div>

            <textarea
                value={
                bulkCandidateText
                }
                onChange={
                e => {

                    setBulkCandidateText(
                    e.target.value
                    )

                    setParsedBulkCandidates(
                    []
                    )

                    setBulkCandidateError(
                    ""
                    )

                }
                }
                placeholder="John Doe John.Doe@abc.com; Jane Doe Jane.Doe@abc.com;"
                className="w-full rounded p-2 m-1 text-xs"
                rows={5}
            />

            <div className="bg-white rounded m-1 px-1 py-3">

                <EtBtnFormBasic
                name={"Preview Candidates"}
                type={"create"}
                handleClick={
                    handleParseBulkCandidates
                }
                disabled={
                    !bulkCandidateText
                    ?.trim()
                }
                />

            </div>

            {bulkCandidateError && (

                <div className="bg-orange-50 rounded m-1 p-2">

                <p className="text-xs text-orange-700">
                    {
                    bulkCandidateError
                    }
                </p>

                </div>

            )}

            {parsedBulkCandidates
                ?.length >
                0 && (

                <div className="bg-white rounded m-1 p-2">

                <p className="font-bold text-xs mb-2">
                    Candidate Preview
                </p>

                {parsedBulkCandidates
                    .map(
                    (
                        candidate,
                        index
                    ) => (

                        <div
                        key={
                            `${candidate.candidateEmail}-${index}`
                        }
                        className="border-b border-gray-100 py-2"
                        >

                        <p className="text-xs font-bold text-gray-700">
                            Candidate {
                            index +
                            1
                            }:{" "}
                            {
                            candidate
                                .candidateName
                            }
                        </p>

                        <p className="text-[10px] text-gray-500 mt-1">
                            {
                            candidate
                                .candidateEmail
                            }
                        </p>

                        </div>

                    )
                    )}

                <div className="mt-3">

                    <EtBtnFormBasic
                    name={
                        `Add ${parsedBulkCandidates.length} Candidate${
                        parsedBulkCandidates.length ===
                        1
                            ? ""
                            : "s"
                        }`
                    }
                    type={"create"}
                    handleClick={
                        handleAddBulkCandidates
                    }
                    disabled={
                        isAddingBulkCandidates ||
                        parsedBulkCandidates
                        .some(
                            candidate =>
                            !candidate
                                ?.candidateName ||
                            !isValidEmail(
                                candidate
                                ?.candidateEmail
                            )
                        )
                    }
                    />

                </div>

                </div>

            )}

           </div>

          {/* Add / Edit Candidate */}

          <div className="bg-gray-200 rounded m-1 p-1">

            <div className="flex justify-between items-center m-1 p-1">

              <p className="font-bold text-xs">

                {
                  editCandidateId
                    ? "Update Candidate"
                    : "Add Candidate"
                }

              </p>

              {editCandidateId && (

                <XMarkIcon
                  onClick={
                    handleCancelEdit
                  }
                  className="h-5 w-5 text-orange-600 cursor-pointer"
                />

              )}

            </div>

            <form
              onSubmit={
                handleSubmit
              }
              className="m-1 p-1 text-xs font-poppins"
            >

              {/* Name */}

              <input
                type="text"
                name="candidateName"
                placeholder="Candidate name"
                value={
                  formData.candidateName
                }
                onChange={
                  handleChange
                }
                className="w-full rounded p-2 m-1"
              />

              {/* Email */}

              <input
                type="email"
                name="candidateEmail"
                placeholder="Candidate email"
                value={
                  formData.candidateEmail
                }
                onChange={
                  handleChange
                }
                className="w-full rounded p-2 m-1"
              />

              {/* Active */}

              <div className="bg-white rounded m-1 p-2">

                <div className="flex justify-start items-center">

                  <input
                    type="checkbox"
                    name="isActive"
                    checked={
                      formData.isActive
                    }
                    onChange={
                      handleChange
                    }
                    className="m-1"
                  />

                  <p className="text-xs font-bold text-gray-700 m-1">
                    Active Candidate
                  </p>

                </div>

              </div>

              {/* Actions */}

              <div
                id="actions"
                className="bg-white m-1 px-1 py-3 rounded"
              >

                <EtBtnFormBasic
                  name={
                    editCandidateId
                      ? "Update Candidate"
                      : "Add Candidate"
                  }
                  type={"create"}
                  handleClick={
                    handleSubmit
                  }
                  disabled={
                    isCreating ||
                    isUpdating ||
                    !formData
                      .candidateName
                      ?.trim() ||
                    !formData
                      .candidateEmail
                      ?.trim()
                  }
                />

                {editCandidateId && (

                  <EtBtnFormBasic
                    name={"Cancel Edit"}
                    type={"cancel"}
                    handleClick={
                      handleCancelEdit
                    }
                  />

                )}

              </div>

            </form>

          </div>

        </div>

      )}

      {/* Delete Confirmation */}

      <Modal
        isOpen={
          isDeleteModalOpen
        }
        onClose={
          handleCloseDeleteModal
        }
        onConfirm={
          handleDeleteCandidate
        }
      />

    </div>

  )

}

export default EtOrgIntentionCandidateManager